import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calculator, TrendingUp, Sparkles } from "lucide-react";

const PLANS = [
    { id: "free", name: "Free", price: 0, min_leads: 0 },
    { id: "starter", name: "Starter", price: 29, min_leads: 1 },
    { id: "pro", name: "Pro", price: 79, min_leads: 50 },
    { id: "business", name: "Business", price: 149, min_leads: 500 },
    { id: "enterprise", name: "Enterprise", price: 499, min_leads: 2000 },
];

function recommendPlan(leads) {
    // Match user spec: <50 free/starter, 50-500 pro, 500-2000 business, >2000 enterprise
    if (leads < 25) return PLANS[0];
    if (leads < 50) return PLANS[1];
    if (leads < 500) return PLANS[2];
    if (leads < 2000) return PLANS[3];
    return PLANS[4];
}

/**
 * Deterministic ROI simulator (no LLM). Placed under the plan cards on
 * /pricing. Sliders drive live output. Recommended plan lights up
 * dynamically and CTA text adapts.
 */
export default function ROISimulator() {
    const { t } = useTranslation();
    const [leads, setLeads] = useState(100);
    const [rate, setRate] = useState(2);
    const [deal, setDeal] = useState(2000);

    const output = useMemo(() => {
        const revenue = Math.round(leads * (rate / 100) * deal);
        const plan = recommendPlan(leads);
        const planCost = plan.price;
        const roi = planCost > 0
            ? Math.round(((revenue - planCost) / planCost) * 100)
            : null;
        const dealsToBreakeven = planCost > 0
            ? Math.max(1, Math.ceil(planCost / deal))
            : 0;
        return { revenue, plan, planCost, roi, dealsToBreakeven };
    }, [leads, rate, deal]);

    return (
        <section
            data-testid="roi-simulator"
            className="mt-24 rounded-3xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-ai)] to-[var(--color-card)] p-8 md:p-12"
        >
            <div className="mb-8 flex items-start gap-3">
                <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] p-2">
                    <Calculator className="h-5 w-5 text-[var(--color-secondary)]" />
                </span>
                <div>
                    <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                        ROI simulator
                    </p>
                    <h2 className="text-2xl font-semibold mt-1">
                        {t("roi.title")}
                    </h2>
                    <p className="text-sm text-text-secondary mt-1">
                        {t("roi.subtitle")}
                    </p>
                </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
                {/* Sliders */}
                <div className="space-y-6">
                    <SliderRow
                        testId="roi-input-leads"
                        label={t("roi.leads_label")}
                        min={10}
                        max={1000}
                        step={10}
                        value={leads}
                        onChange={setLeads}
                        format={(v) => v.toLocaleString()}
                    />
                    <SliderRow
                        testId="roi-input-rate"
                        label={t("roi.rate_label")}
                        min={0.5}
                        max={10}
                        step={0.1}
                        value={rate}
                        onChange={setRate}
                        format={(v) => `${v.toFixed(1)}%`}
                    />
                    <SliderRow
                        testId="roi-input-deal"
                        label={t("roi.deal_label")}
                        min={100}
                        max={50000}
                        step={100}
                        value={deal}
                        onChange={setDeal}
                        format={(v) => `$${v.toLocaleString()}`}
                    />
                </div>

                {/* Output */}
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-6 space-y-4">
                    <div data-testid="roi-output-revenue">
                        <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                            {t("roi.revenue_label")}
                        </p>
                        <p className="text-4xl font-semibold text-[var(--color-success)] mt-1 tabular-nums">
                            ${output.revenue.toLocaleString()}
                        </p>
                    </div>

                    <div
                        data-testid="roi-output-recommendation"
                        className="rounded-xl border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-4"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="h-4 w-4 text-[var(--color-primary)]" />
                            <p className="text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
                                {t("roi.plan_label")}
                            </p>
                        </div>
                        <p className="text-2xl font-semibold">
                            {output.plan.name}
                            <span className="text-sm font-normal text-text-secondary ml-2">
                                {output.planCost > 0 ? `$${output.planCost}/mo` : "$0"}
                            </span>
                        </p>
                        <p className="text-xs text-text-secondary mt-2">
                            {output.planCost === 0
                                ? t("roi.cta_free_hint")
                                : t("roi.cta_paid_hint", { n: output.dealsToBreakeven })}
                        </p>
                        <Link
                            to="/app/subscription"
                            data-testid="roi-cta-choose"
                            className="mt-3 inline-flex items-center gap-1.5 text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                        >
                            <TrendingUp className="h-3 w-3" />
                            {t("roi.cta_choose")}
                        </Link>
                    </div>

                    {output.roi !== null && output.roi > 0 && (
                        <div data-testid="roi-output-roi">
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                                {t("roi.roi_label")}
                            </p>
                            <p className="text-lg font-semibold text-text-main tabular-nums mt-1">
                                {output.roi.toLocaleString()}%
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function SliderRow({ testId, label, min, max, step, value, onChange, format }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-text-secondary">{label}</label>
                <span className="text-sm font-medium tabular-nums">
                    {format ? format(value) : value}
                </span>
            </div>
            <input
                data-testid={testId}
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full accent-[var(--color-primary)]"
            />
        </div>
    );
}
